import React, { useState, useEffect, useRef } from 'react';
import { string, arrayOf, shape, func, bool } from 'prop-types';
import { GlobalRemoteEnum } from '@core/types';
import { useTranslation, useEnums, useWrapperDimensions } from '@core/hooks';
import Button from 'devextreme-react/button';
// devExtreme
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import TextArea from 'devextreme-react/text-area';
import {
  ActionBtns,
  LabeledItem,
  ExtendedHtmlEditor,
  Spinner
} from '@core/components';
import { XUserInteraction, XDevExtreme } from '@core/utils';
import { NotifyType } from '@core/utils/src/userInteraction/notifications/types';

// api
import { updateWiadomoscSzablon } from '@modules/raccoon/src/api/wiadomosciSzablony';

// utils
import { findJezyk, dataHasChanged, prepareJezyki } from './utils/data';

const MainPopup = ({ data, setShowPopup, setPopupTitle, ready }) => {
  const titleRef = useRef(null);
  const smsTextRef = useRef(null);

  const [isPreview, setIsPreview] = useState(false);
  const [previewData, setPreviewData] = useState('');
  const [rowData, setRowData] = useState({ ...data });
  const [selectBoxVal, setSelectBoxVal] = useState('');
  const [htmlEditorInstance, setHtmlEditorInstance] = useState(null);
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [textAreaInstance, setTextAreaInstance] = useState(null);

  const { t } = useTranslation();

  const langs = useEnums(GlobalRemoteEnum.JEZYK_TYP);

  const isEmailEditor = rowData.WiadomoscTyp === 'Inne';
  const isHtmlPreview = rowData.WiadomoscTyp === 'Email';

  const [htmlWrapperRef, htmlWrapperDimensions] = useWrapperDimensions(
    ready && isEmailEditor
  );

  useEffect(() => {
    setSelectBoxVal(langs[0].id);
    setPopupTitle(`${rowData.Id} - ${t('Edycja')}`);
  }, []);

  const setTrescVal = (newTresc = '') => {
    if (isEmailEditor) {
      htmlEditorInstance.option('value', newTresc);
      textAreaInstance.option('value', newTresc);
    } else {
      smsTextRef.current?.option('value', newTresc);
    }
  };

  const togglePreview = () => {
    setIsPreview((prev) => !prev);
  };

  useEffect(() => {
    if (!ready) return;

    const htmlEditorNotLoaded =
      !htmlWrapperDimensions.height || !htmlEditorInstance;

    if (isEmailEditor && htmlEditorNotLoaded) {
      return;
    }

    if (selectBoxVal === '') return;

    const Jezyk = findJezyk(rowData.Jezyki, selectBoxVal);

    if (Jezyk) {
      setTrescVal(Jezyk.Tresc);

      titleRef.current.option('value', Jezyk.Tytul);
    } else {
      setTrescVal();
      titleRef.current.option('value', '');
    }
  }, [selectBoxVal, ready, htmlWrapperDimensions.height, htmlEditorInstance]);

  const handleSelectBoxChange = ({ itemData: { id } }) => {
    if (selectBoxVal !== id) {
      const dataChanged = dataHasChanged(
        rowData,
        selectBoxVal,
        htmlEditorInstance,
        textAreaInstance,
        titleRef,
        smsTextRef,
        showRawHtml
      );

      if (dataChanged) {
        XUserInteraction.confirmDialog(
          t('ZmianaJezykaZmianyUtraconeConfirm'),
          () => setSelectBoxVal(id)
        );
      } else {
        setSelectBoxVal(id);
      }
    }
  };

  const updateData = () => {
    const newJezyki = prepareJezyki(
      rowData,
      showRawHtml,
      textAreaInstance,
      htmlEditorInstance,
      smsTextRef,
      titleRef,
      selectBoxVal
    );

    XUserInteraction.notifyUser({ msg: t('Zapisuje') });

    updateWiadomoscSzablon(rowData.Id, {
      Jezyki: newJezyki
    })
      .then(() => {
        XUserInteraction.notifyUser({
          msg: t('Zapisano'),
          type: NotifyType.SUCCESS
        });
        setRowData({ ...rowData, Jezyki: newJezyki });
        setShowPopup(false);
      })
      .catch(XUserInteraction.notifyError);
  };

  const handleCancel = () => {
    const dataChanged = dataHasChanged(
      rowData,
      selectBoxVal,
      htmlEditorInstance,
      textAreaInstance,
      titleRef,
      smsTextRef,
      showRawHtml
    );

    if (dataChanged) {
      XUserInteraction.confirmDialog(t('AnulujConfirm'), () => {
        setShowPopup(false);
      });
    } else {
      setShowPopup(false);
    }
  };

  if (!ready) {
    return <Spinner />;
  }

  return (
    <div className="szablon-form-wrapper">
      <div className="szablon-form">
        <SelectBox
          dataSource={{
            store: {
              type: 'array',
              data: langs,
              key: 'id'
            }
          }}
          onItemClick={handleSelectBoxChange}
          value={selectBoxVal}
          displayExpr="name"
          valueExpr="id"
        />
        <LabeledItem labelText={t('Tytul')}>
          <TextBox
            onInitialized={(e) => XDevExtreme.setInstanceToRef(titleRef, e)}
          />
        </LabeledItem>
        {isHtmlPreview && (
          <Button
            onClick={togglePreview}
            className="szablon-form__preview-button"
            icon={isPreview ? 'close' : 'find'}
          />
        )}
        {isEmailEditor ? (
          <div ref={htmlWrapperRef} className="szablon-form__html-wrapper">
            {!!htmlWrapperDimensions.height && (
              <ExtendedHtmlEditor
                wrapperStyle={{ maxWidth: htmlWrapperDimensions.width }}
                height={!showRawHtml ? htmlWrapperDimensions.height : 78}
                showRawHtml={showRawHtml}
                setShowRawHtml={setShowRawHtml}
                htmlEditorInstance={htmlEditorInstance}
                setHtmlEditorInstance={setHtmlEditorInstance}
                textAreaInstance={textAreaInstance}
                setTextAreaInstance={setTextAreaInstance}
              />
            )}
          </div>
        ) : (
          <div className="szablon-form__preview-wrapper">
            {isPreview && isHtmlPreview && (
              <iframe
                className="szablon-form__preview"
                title="preview"
                srcDoc={previewData}
              />
            )}
            <TextArea
              height="100%"
              valueChangeEvent="keyup"
              onValueChanged={(e) => {
                setPreviewData(e.value);
              }}
              onInitialized={(e) => XDevExtreme.setInstanceToRef(smsTextRef, e)}
            />
          </div>
        )}
      </div>
      <ActionBtns
        customCancelText={t('Zamknij')}
        handleSave={updateData}
        handleCancel={handleCancel}
      />
    </div>
  );
};

MainPopup.propTypes = {
  setShowPopup: func.isRequired,
  setPopupTitle: func.isRequired,
  ready: bool.isRequired,
  data: shape({
    Typ: string,
    Jezyki: arrayOf(shape({}))
  })
};

export default MainPopup;
